import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, RotateCcw, Mail, Phone } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export default function SupportTicketsPanel() {
  const [status, setStatus] = useState("open");
  const [data, setData] = useState(null);
  const [sel, setSel] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/admin/support/tickets?status=${status}`).then(({ data }) => setData(data)).catch(() => toast.error("Could not load tickets"));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [status]);

  const resolve = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/support/tickets/${sel.id}/resolve`, { note });
      toast.success("Ticket resolved"); setSel(null); setNote(""); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const reopen = async (id) => {
    try { await api.post(`/admin/support/tickets/${id}/reopen`); toast.success("Ticket reopened"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  return (
    <section className="mt-14" data-testid="admin-support">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-[#C85A32]">Help &amp; Support</div>
          <h2 className="font-display text-3xl mt-1">Support tickets</h2>
        </div>
        {data && <div className="text-sm text-[#5C6B7A]" data-testid="support-counts"><span className="text-[#C85A32] font-semibold">{data.counts.open}</span> open · {data.counts.resolved} resolved</div>}
      </div>
      <div className="mt-4 inline-flex rounded-md bg-[#F0EBE1] p-1 text-sm">
        {["open", "resolved", "all"].map((s) => (
          <button key={s} onClick={() => setStatus(s)} className={`px-4 py-1.5 rounded capitalize ${status === s ? "bg-[#FDFBF7] shadow-sm font-semibold" : "text-[#5C6B7A]"}`} data-testid={`support-filter-${s}`}>{s}</button>
        ))}
      </div>
      {!data ? <div className="mt-4 text-sm text-[#5C6B7A]">Loading…</div> : data.items.length === 0 ? (
        <div className="mt-4 bg-[#F0EBE1] rounded-lg p-8 text-center text-[#5C6B7A]" data-testid="support-empty">No {status === "all" ? "" : status} tickets.</div>
      ) : (
        <div className="mt-4 space-y-3" data-testid="support-list">
          {data.items.map((t) => (
            <div key={t.id} className="rounded-lg border border-[#1A1F2C]/10 bg-[#F0EBE1] p-4 flex gap-4 flex-wrap" data-testid={`ticket-${t.id}`}>
              <div className="flex-1 min-w-[240px]">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`px-2 py-0.5 rounded text-[10px] uppercase tracking-[0.16em] ${t.status === "resolved" ? "bg-[#D4DEC6] text-[#2B3A67]" : "bg-[#C85A32]/15 text-[#C85A32]"}`} data-testid={`ticket-status-${t.id}`}>{t.status}</span>
                  <span className="text-xs font-mono text-[#5C6B7A]">#{t.id.slice(0, 8)}</span>
                  <span className="text-xs capitalize px-2 py-0.5 rounded bg-[#FDFBF7] border border-[#1A1F2C]/10">{t.issue_type}</span>
                  <span className="text-xs text-[#5C6B7A]">{new Date(t.created_at).toLocaleString()}</span>
                </div>
                <div className="mt-2 font-medium">{t.name} <span className="text-xs text-[#5C6B7A] font-normal">({t.user_role || "guest"})</span></div>
                <div className="text-xs text-[#5C6B7A] flex items-center gap-3 flex-wrap">
                  <a href={`mailto:${t.email}?subject=Re: LocaNest ticket #${t.id.slice(0, 8)}`} className="inline-flex items-center gap-1 hover:text-[#C85A32]"><Mail size={11} /> {t.email}</a>
                  {t.phone && <a href={`tel:+91${t.phone}`} className="inline-flex items-center gap-1 hover:text-[#C85A32]"><Phone size={11} /> {t.phone}</a>}
                </div>
                <p className="mt-2 text-sm whitespace-pre-wrap">{t.message}</p>
                {t.resolution_note && <div className="mt-2 text-xs text-[#2B3A67]">Resolution: {t.resolution_note}</div>}
              </div>
              <div className="flex md:flex-col gap-2 justify-end">
                {t.status === "resolved" ? (
                  <Button size="sm" variant="outline" onClick={() => reopen(t.id)} className="rounded-md" data-testid={`ticket-reopen-${t.id}`}><RotateCcw size={12} className="mr-1" /> Reopen</Button>
                ) : (
                  <Button size="sm" onClick={() => setSel(t)} className="bg-[#2B3A67] hover:bg-[#1f2c52] text-white rounded-md" data-testid={`ticket-resolve-${t.id}`}><CheckCircle2 size={12} className="mr-1" /> Resolve</Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      <Dialog open={!!sel} onOpenChange={(v) => !v && setSel(null)}>
        <DialogContent data-testid="ticket-dialog">
          <DialogHeader><DialogTitle>Resolve ticket #{sel?.id?.slice(0, 8)}</DialogTitle><DialogDescription>Add a short note on how it was resolved (internal).</DialogDescription></DialogHeader>
          <Textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Refund initiated via Cashfree, UTR …" data-testid="ticket-note" />
          <DialogFooter><Button onClick={resolve} disabled={busy} className="bg-[#2B3A67] hover:bg-[#1f2c52] text-white" data-testid="ticket-resolve-confirm">{busy ? "Saving…" : "Mark resolved"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}
